<template>
  <div class="v2-address-form">
    <validation-observer
      ref="formObserver"
      #default="{ invalid }"
    >
      <div class="v2-address-form__grid">
        <!-- Address Line 1 -->
        <validation-provider
          #default="{ errors }"
          name="Address Line 1"
          rules="required"
        >
          <v2-input
            v-model="localAddress.addressLine1"
            label="Address Line 1"
            placeholder="Enter Address Line 1"
            :disabled="disabled"
            :error="errors[0]"
          />
        </validation-provider>

        <!-- Address Line 2 -->
        <validation-provider
          #default="{ errors }"
          name="Address Line 2"
          rules="required"
        >
          <v2-input
            v-model="localAddress.addressLine2"
            label="Address Line 2"
            placeholder="Enter Address Line 2"
            :disabled="disabled"
            :error="errors[0]"
          />
        </validation-provider>

        <!-- City -->
        <validation-provider
          #default="{ errors }"
          name="City"
          rules="required"
        >
          <v2-input
            v-model="localAddress.city"
            label="City"
            placeholder="Enter City"
            :disabled="disabled"
            :error="errors[0]"
          />
        </validation-provider>

        <!-- Country -->
        <validation-provider
          #default="{ errors }"
          name="Country"
          rules="required"
        >
          <div class="v2-input-wrapper">
            <label class="v2-input__label">Country</label>
            <v2-dropdown
              :value="localAddress.country"
              :options="countries"
              placeholder="Select Country"
              :searchable="true"
              :disabled="disabled"
              @select="val => localAddress.country = val"
            />
            <small
              v-if="errors[0]"
              class="v2-address-form__error"
            >{{ errors[0] }}</small>
          </div>
        </validation-provider>

        <!-- Zipcode -->
        <validation-provider
          #default="{ errors }"
          name="Zipcode"
          rules="required"
        >
          <v2-input
            v-model="localAddress.zipcode"
            label="Zipcode"
            placeholder="Enter Zipcode"
            :disabled="disabled"
            :error="errors[0]"
          />
        </validation-provider>

        <!-- Contact Name -->
        <validation-provider
          #default="{ errors }"
          name="Address Contact Name"
          rules="required"
        >
          <v2-input
            v-model="localAddress.contactName"
            label="Address Contact Name"
            placeholder="Enter Address Contact Name"
            :disabled="disabled"
            :error="errors[0]"
          />
        </validation-provider>

        <!-- Contact Number -->
        <div class="v2-input-wrapper">
          <label class="v2-input__label">Address Contact Number</label>
          <vue-tel-input
            v-model="localAddress.contactNumber"
            :input-options="telInputOptions"
            mode="international"
            :disabled="disabled"
            :class="['v2-tel-input', { 'v2-tel-input--disabled': disabled }]"
          />
        </div>

        <!-- Contact Email -->
        <validation-provider
          #default="{ errors }"
          name="Address Contact Email"
          rules="required|email"
        >
          <v2-input
            v-model="localAddress.contactEmail"
            label="Address Contact Email"
            type="email"
            placeholder="Enter Address Contact Email"
            :disabled="disabled"
            :error="errors[0]"
          />
        </validation-provider>
      </div>

      <!-- Actions -->
      <div class="v2-address-form__actions">
        <v2-button
          variant="primary"
          :loading="saving"
          :disabled="invalid || disabled"
          @click="onSave"
        >
          Save
        </v2-button>
        <v2-button
          v-if="showCancel"
          variant="outline"
          :disabled="saving"
          @click="$emit('cancel')"
        >
          Cancel
        </v2-button>
        <v2-button
          v-if="showReset"
          variant="outline"
          :disabled="saving"
          @click="onReset"
        >
          Cancel
        </v2-button>
      </div>
    </validation-observer>
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import V2Input from '@/components/v2/V2Input.vue'
import V2Dropdown from '@/components/v2/V2Dropdown.vue'
import V2Button from '@/components/v2/V2Button.vue'

export default {
  name: 'V2AddressForm',
  components: {
    ValidationProvider,
    ValidationObserver,
    V2Input,
    V2Dropdown,
    V2Button,
  },
  props: {
    address: {
      type: Object,
      required: true,
    },
    countries: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    showCancel: {
      type: Boolean,
      default: true,
    },
    showReset: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      localAddress: JSON.parse(JSON.stringify(this.address)),
      cleanAddress: JSON.parse(JSON.stringify(this.address)),
      saving: false,
      telInputOptions: {
        placeholder: 'Enter phone number',
      },
    }
  },
  computed: {
    isDirty() {
      return JSON.stringify(this.localAddress) !== JSON.stringify(this.cleanAddress)
    },
  },
  watch: {
    address: {
      handler(val) {
        if (!this.isDirty) {
          this.localAddress = JSON.parse(JSON.stringify(val))
          this.cleanAddress = JSON.parse(JSON.stringify(val))
        }
      },
      deep: true,
    },
  },
  methods: {
    async onSave() {
      const valid = await this.$refs.formObserver.validate()
      if (!valid) return
      this.saving = true
      const done = () => { this.saving = false }
      this.$emit('save', { ...this.localAddress }, done)
    },
    onReset() {
      this.localAddress = JSON.parse(JSON.stringify(this.address))
      this.cleanAddress = JSON.parse(JSON.stringify(this.address))
      this.$refs.formObserver.reset()
    },
  },
}
</script>

<style lang="scss" scoped>
.v2-address-form {
  &__grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--v2-space-4);
  }

  &__actions {
    display: flex;
    gap: var(--v2-space-3);
    margin-top: var(--v2-space-5);
  }

  &__error {
    font-size: 0.75rem;
    color: var(--v2-destructive);
  }
}
</style>

<style lang="scss">
// vue-tel-input V2 theme (unscoped so it can target child elements)
.v2-address-form .v2-tel-input {
  &.vue-tel-input {
    height: 2.714rem;
    border: 1px solid var(--v2-glass-stroke);
    border-radius: var(--v2-radius-md);
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    background: var(--v2-glass-surface-2);

    &:focus-within {
      border-color: var(--v2-primary);
      box-shadow: none;
    }

    .vti__dropdown {
      border-radius: var(--v2-radius-md) 0 0 var(--v2-radius-md);
      background: transparent;
    }

    .vti__dropdown-list {
      z-index: 200;
      border: 1px solid var(--v2-glass-stroke);
      border-radius: var(--v2-radius-md);
      box-shadow: var(--v2-glass-shadow-floating);
      background: var(--v2-glass-surface-floating);
    }

    // Inner text input — transparent so the wrapper's glass shows through.
    // Right-side rounding so the input edge matches the wrapper's
    // border-radius (avoids the visual mismatch on the top-right and
    // bottom-right corners where the input was overflowing the wrapper).
    .vti__input {
      font-family: var(--v2-font-sans);
      font-size: 0.875rem;
      color: var(--v2-foreground);
      background: transparent;
      border: none;
      border-radius: 0 var(--v2-radius-md) var(--v2-radius-md) 0;

      &::placeholder {
        color: var(--v2-muted-foreground);
      }
    }
  }

  &--disabled > * {
    cursor: not-allowed !important;
  }

  &--disabled > div.vti__dropdown:hover {
    background-color: unset !important;
  }
}
</style>
